import { useState, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Camera, Upload } from 'lucide-react';

export default function ProfilePictureModal({ user, onClose, onUpdated }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(user.profile_picture || '');
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!preview || preview === user.profile_picture) {
      toast.error('Please select a new image');
      return;
    }

    setUploading(true);
    try {
      await axios.put('/users/profile-picture', {
        profile_picture: preview
      });
      toast.success('Profile picture updated!');
      onUpdated();
      onClose();
    } catch (error) {
      toast.error('Failed to update profile picture');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="profile-picture-modal">
        <DialogHeader>
          <DialogTitle>Upload Profile Picture</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex flex-col items-center gap-4">
            {/* Preview */}
            <div className="relative">
              {preview ? (
                <img
                  src={preview}
                  alt="Profile preview"
                  className="w-32 h-32 rounded-full object-cover border-4 border-purple-500"
                  data-testid="profile-picture-preview"
                />
              ) : (
                <div className="w-32 h-32 rounded-full bg-purple-600 flex items-center justify-center border-4 border-purple-500">
                  <span className="text-white text-4xl font-bold">
                    {user.name.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="absolute bottom-0 right-0 bg-purple-600 rounded-full p-2 hover:bg-purple-700 transition-colors"
                data-testid="upload-button"
              >
                <Camera className="w-5 h-5 text-white" />
              </button>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="file-input"
            />

            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                Click the camera icon to select an image
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Max size: 10MB • JPG, PNG, GIF
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={uploading || !preview || preview === user.profile_picture}
              className="flex-1"
              data-testid="save-picture-button"
            >
              {uploading ? 'Uploading...' : 'Save Picture'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
